import { useEffect, useMemo, useState } from 'react'
import {
  archiveProjects,
  allStacks,
  groupByMonth,
  formatMonth,
  type ArchiveProject,
} from '@content/archiveProjects'

function matchesFilters(project: ArchiveProject, query: string, activeStacks: Set<string>): boolean {
  if (activeStacks.size > 0) {
    const hasAll = Array.from(activeStacks).every((s) => project.stack.includes(s))
    if (!hasAll) return false
  }
  if (!query) return true
  const q = query.toLowerCase()
  if (project.name.toLowerCase().includes(q)) return true
  if (project.description.toLowerCase().includes(q)) return true
  if (project.stack.some((s) => s.toLowerCase().includes(q))) return true
  return false
}

export function ArchiveView({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState('')
  const [activeStacks, setActiveStacks] = useState<Set<string>>(new Set())

  const stacks = useMemo(() => allStacks(archiveProjects), [])
  const filtered = useMemo(
    () => archiveProjects.filter((p) => matchesFilters(p, query, activeStacks)),
    [query, activeStacks]
  )
  const grouped = useMemo(() => groupByMonth(filtered), [filtered])

  useEffect(() => {
    window.scrollTo(0, 0)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onBack() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBack])

  const toggleStack = (stack: string) => {
    setActiveStacks((prev) => {
      const next = new Set(prev)
      if (next.has(stack)) next.delete(stack)
      else next.add(stack)
      return next
    })
  }

  const clearFilters = () => {
    setQuery('')
    setActiveStacks(new Set())
  }

  const totalCount = archiveProjects.length
  const filteredCount = filtered.length

  return (
    <div className="modern-mode">
      <nav className="modern-nav modern-nav--scrolled">
        <button className="modern-nav__logo modern-article__back" onClick={onBack}>
          &larr; Back
        </button>
      </nav>
      <main className="modern-archive">
        <header className="modern-archive__header">
          <p className="modern-section__label">Archive</p>
          <h1 className="modern-archive__title">All projects</h1>
          <p className="modern-archive__intro">
            {totalCount} projects with 2025 Q4-present activity, spanning {stacks.length} stacks.
            Curated from local git history; the featured 12 on the main page are a hand-picked subset.
          </p>
        </header>

        <div className="modern-archive__controls">
          <label className="modern-archive__search">
            <span className="sr-only">Search projects</span>
            <input
              type="search"
              className="modern-archive__search-input"
              placeholder="Search by name, description, or stack..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="modern-archive__filters" role="group" aria-label="Filter by stack">
            {stacks.map((stack) => {
              const active = activeStacks.has(stack)
              return (
                <button
                  key={stack}
                  type="button"
                  className={`modern-archive__filter${active ? ' modern-archive__filter--active' : ''}`}
                  onClick={() => toggleStack(stack)}
                  aria-pressed={active}
                >
                  {stack}
                </button>
              )
            })}
          </div>
          <div className="modern-archive__status">
            <span>
              Showing {filteredCount} of {totalCount}
            </span>
            {(query || activeStacks.size > 0) && (
              <button type="button" className="modern-archive__clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="modern-archive__empty">
            No projects match those filters. Try removing a stack or clearing the search.
          </p>
        ) : (
          Array.from(grouped.entries()).map(([month, items]) => (
            <section key={month} className="modern-archive__group">
              <h2 className="modern-archive__group-title">{formatMonth(month)}</h2>
              <ul className="modern-archive__list">
                {items.map((p) => (
                  <li key={`${p.name}-${p.month}`} className="modern-archive__item">
                    <div className="modern-archive__item-head">
                      <h3 className="modern-archive__item-name">{p.name}</h3>
                      <div className="modern-archive__item-badges">
                        {p.featured && <span className="modern-archive__badge modern-archive__badge--featured">Featured</span>}
                        {p.hasStarDoc && <span className="modern-archive__badge">STAR</span>}
                      </div>
                    </div>
                    <p className="modern-archive__item-desc">{p.description}</p>
                    <div className="modern-archive__item-stack">
                      {p.stack.map((s) => (
                        <span key={s} className="modern-card__tech-tag">{s}</span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </main>
    </div>
  )
}
